import _ from 'lodash';

export const acresPerSqMeter = 0.000247105;
export const feetPerMile = 5280;
export const acresPerSqFt = 1 / 43560;
export const ftPerMeter = 3.28084;
export const metersPerKm = 1000;

/* The following "unit specs" are maps of unit objects keyed by unit system
 * (default, metric). The unit objects have the following properties:
 * - display: how to display the unit
 * - displayCsv: (optional) how to display the unit in CSV file
 * - multiplier: how to convert to the unit from the default unit
 */

export const SMALL_DISTANCE_UNITS = {
  default: {
    display: 'in',
  },
  metric: {
    display: 'cm',
    multiplier: 2.54,
  },
};

export const DISTANCE_UNITS = {
  default: {
    display: 'ft',
  },
  metric: {
    display: 'm',
    multiplier: 0.3048,
  },
};

export const LARGE_DISTANCE_UNITS = {
  default: {
    display: 'mi',
  },
  metric: {
    display: 'km',
    multiplier: 1.60934,
  },
};

export const ROW_UNITS = {
  default: {
    display: 'row ft',
  },
  metric: {
    display: 'row m',
    multiplier: 0.3048,
  },
};

export const YIELD_WEIGHT_UNITS = {
  default: {
    display: 'lbs',
  },
  metric: {
    display: 'kg',
    multiplier: 0.453592,
  },
};

export const YIELD_WEIGHT_SINGULAR_UNITS = {
  default: {
    display: 'lb',
  },
  metric: {
    display: 'kg',
    multiplier: 0.453592,
  },
};

export const YIELD_VOLUME_UNITS = {
  default: {
    display: 'bft',
  },
  metric: {
    display: 'm³',
    displayCsv: 'm^3',
    multiplier: 0.00235974,
  },
};

export const SMALL_AREA_UNITS = {
  default: {
    display: 'sqft',
  },
  metric: {
    display: 'm²',
    displayCsv: 'm^2',
    multiplier: 0.092903,
  },
};

export const AREA_UNITS = {
  default: {
    display: 'ac',
  },
  metric: {
    display: 'ha',
    multiplier: 0.404686,
  },
};

export const LONG_AREA_UNITS = {
  default: {
    display: 'acres',
  },
  metric: {
    display: 'hectares',
    multiplier: 0.404686,
  },
};

export const LIQUID_VOLUME_UNITS = {
  default: {
    display: 'gal',
  },
  metric: {
    display: 'L',
    multiplier: 3.78541,
  },
};

export const DBH_UNITS = {
  default: {
    display: 'in',
  },
  metric: {
    display: 'cm',
    multiplier: 2.54,
  },
};

/* The following unit spec maps are maps of unit specs keyed by default unit string. */

export const YIELD_UNITS_BY_DEFAULT = _.keyBy(
  [YIELD_WEIGHT_UNITS, YIELD_VOLUME_UNITS],
  'default.display',
);

// Revenue uses singular units for weight.
export const REVENUE_UNITS_BY_DEFAULT = _.keyBy(
  [YIELD_WEIGHT_SINGULAR_UNITS, YIELD_VOLUME_UNITS],
  'default.display',
);

/*
Apply to a unitSpec to get the unitSpec for the inverse unit.
Used for price per unit, duration per unit, etc.
  eg. gal -> /gal, L -> /L
*/
export const invertUnitSpec = (unitSpec, prefix = '', unitPrefix = '') => {
  if (!unitSpec) return null;
  return Object.entries(unitSpec).reduce(
    (acc, [key, { display, displayCsv, multiplier = 1 }]) => ({
      ...acc,
      [key]: {
        display: `${prefix}/${unitPrefix}${display}`,
        displayCsv: `${prefix}/${unitPrefix}${displayCsv || display}`,
        multiplier: 1 / multiplier,
      },
    }),
    {},
  );
};

// Inverts all of the unit spec values in the map. Leaves the keys the same.
export const invertUnitSpecMap = (specMap) =>
  Object.entries(specMap).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: invertUnitSpec(value),
    }),
    {},
  );

export const INVERTED_LIQUID_VOLUME_UNITS = invertUnitSpec(LIQUID_VOLUME_UNITS);
export const INVERTED_AREA_UNITS = invertUnitSpec(AREA_UNITS);
export const INVERTED_ROW_UNITS = invertUnitSpec(ROW_UNITS);

export const INVERTED_REVENUE_UNITS_BY_DEFAULT = invertUnitSpecMap(
  REVENUE_UNITS_BY_DEFAULT,
);

export const INVERTED_YIELD_UNITS_BY_DEFAULT = invertUnitSpecMap(
  YIELD_UNITS_BY_DEFAULT,
);

export const INVERTED_STEM_AREA_UNITS = invertUnitSpec(
  AREA_UNITS,
  'stems',
  'planted ',
);

// Converts 'default' to the more clear 'imperial'.
export const toReadableUnitName = (unitSystem) =>
  ({ default: 'imperial' }[unitSystem] ?? unitSystem);

// Converts the more clear string 'imperial' to the string we use in unit specs, 'default'.
export const fromReadableUnitName = (unitName) =>
  ({ imperial: 'default' }[unitName] ?? unitName);
